import { FieldGuideConnectionStatusKind, FieldGuidePbcRequestItem } from '../../Models/Api/strongbox.financialportal';
import {
    ConnectWithPat as ConnectWithPatService,
    FieldGuideConnectResult,
    FieldGuideListPbcRequestsResult,
    FieldGuideStatusResult,
    GetConnectionStatus as GetConnectionStatusService,
    ListFieldGuidePbcRequests,
} from '../../Services/FieldGuideService';

import { AppThunkActionAsync } from '../index';
import {
    FieldGuideActions,
    FieldGuideCompanySelection,
    FieldGuideEngagementSelection,
    FieldGuideMappedRequest,
    IBeginConnect,
    IBeginSending,
    IBeginUpload,
    ICancelUpload,
    IConnectFailed,
    IConnectionStatusLoaded,
    IConnectSucceeded,
    INextUploadStep,
    IPreviousUploadStep,
    ISelectCompany,
    ISelectEngagement,
    ISendFailed,
    ISendSucceeded,
    ISetRequestMapping,
    ISetReconnectRequired,
    KnownAction,
} from './Actions';

import { GetFieldGuidePbcRequests, GetFieldGuideSelectedEngagement } from './Selectors';

const toMappedRequest = (item: FieldGuidePbcRequestItem): FieldGuideMappedRequest =>
    ({ id: item.id, title: item.title, description: item.description, url: item.url, status: item.status });

type ListPbcRequestsFn = (engagementId: string, options?: { signal?: AbortSignal }) => Promise<FieldGuideListPbcRequestsResult>;

function BeginConnect(): IBeginConnect {
    return { type: FieldGuideActions.BeginConnect };
}

function ConnectSucceeded(fieldGuideEmail: string | undefined, fieldGuideDisplayName: string | undefined): IConnectSucceeded {
    return {
        type: FieldGuideActions.ConnectSucceeded,
        fieldGuideEmail,
        fieldGuideDisplayName,
    };
}

function ConnectFailed(): IConnectFailed {
    return { type: FieldGuideActions.ConnectFailed };
}

function SetReconnectRequired(): ISetReconnectRequired {
    return { type: FieldGuideActions.SetReconnectRequired };
}

function ConnectionStatusLoaded(
    status: FieldGuideConnectionStatusKind,
    reason: string | undefined,
    fieldGuideEmail: string | undefined,
    fieldGuideDisplayName: string | undefined
): IConnectionStatusLoaded {
    return {
        type: FieldGuideActions.ConnectionStatusLoaded,
        status,
        reason,
        fieldGuideEmail,
        fieldGuideDisplayName,
    };
}

/**
 * Submits a FieldGuide PAT and updates the connection domain from the outcome. Returns the full
 * FieldGuideConnectResult so the caller (the PAT form) can render the specific rejection reason inline;
 * this domain's state only ever ends up at Connected or NotConnected.
 */
const ConnectWithPat = (token: string): AppThunkActionAsync<FieldGuideConnectResult, KnownAction> =>
    async (dispatch): Promise<FieldGuideConnectResult> => {
        dispatch(BeginConnect());

        const result = await ConnectWithPatService(token);

        if (result.succeeded) {
            dispatch(ConnectSucceeded(result.response.fieldGuideEmail, result.response.fieldGuideDisplayName));
        } else {
            dispatch(ConnectFailed());
        }

        return result;
    };

/**
 * Reads the server's connection status and loads it into the connection domain. Dispatches nothing when
 * the status can't be read (unavailable or forbidden), leaving the connection state unchanged; returns the
 * service result so the caller can render those outcomes.
 */
const CheckConnectionStatus = (signal?: AbortSignal): AppThunkActionAsync<FieldGuideStatusResult, KnownAction> =>
    async (dispatch): Promise<FieldGuideStatusResult> => {
        const result = await GetConnectionStatusService({ signal });

        if (result.outcome === 'status' && !signal?.aborted) {
            const { status, reason, fieldGuideEmail, fieldGuideDisplayName } = result.response;
            dispatch(ConnectionStatusLoaded(status, reason, fieldGuideEmail, fieldGuideDisplayName));
        }

        return result;
    };

export const actionCreators = {
    BeginUpload: (): IBeginUpload => ({ type: FieldGuideActions.BeginUpload }),
    SelectCompany: (company: FieldGuideCompanySelection): ISelectCompany => ({ type: FieldGuideActions.SelectCompany, company }),
    SelectEngagement: (engagement: FieldGuideEngagementSelection): ISelectEngagement => ({ type: FieldGuideActions.SelectEngagement, engagement }),
    SetRequestMapping: (variantId: string, request: FieldGuideMappedRequest | undefined, fileName: string): ISetRequestMapping =>
        ({ type: FieldGuideActions.SetRequestMapping, variantId, request, fileName }),
    NextUploadStep: (): INextUploadStep => ({ type: FieldGuideActions.NextUploadStep }),
    PreviousUploadStep: (): IPreviousUploadStep => ({ type: FieldGuideActions.PreviousUploadStep }),
    BeginSending: (): IBeginSending => ({ type: FieldGuideActions.BeginSending }),
    SendSucceeded: (): ISendSucceeded => ({ type: FieldGuideActions.SendSucceeded }),
    SendFailed: (): ISendFailed => ({ type: FieldGuideActions.SendFailed }),
    CancelUpload: (): ICancelUpload => ({ type: FieldGuideActions.CancelUpload }),
    LoadFieldGuidePbcRequests: (list: ListPbcRequestsFn = ListFieldGuidePbcRequests): AppThunkActionAsync<void, KnownAction> =>
        async (dispatch, getState): Promise<void> => {
            const engagementId = GetFieldGuideSelectedEngagement(getState())?.id;
            if (!engagementId) {
                return;
            }

            const existing = GetFieldGuidePbcRequests(getState());
            if (existing && existing.status !== 'failed') {
                return;
            }

            dispatch({ type: FieldGuideActions.LoadPbcRequests, engagementId });

            let result: FieldGuideListPbcRequestsResult;
            try {
                result = await list(engagementId);
            } catch {
                dispatch({ type: FieldGuideActions.PbcRequestsFailed, engagementId });
                return;
            }

            if (result.succeeded) {
                const { requests } = result as { succeeded: true; requests: FieldGuidePbcRequestItem[] };
                dispatch({ type: FieldGuideActions.PbcRequestsLoaded, engagementId, requests: requests.map(toMappedRequest) });
            } else {
                dispatch({ type: FieldGuideActions.PbcRequestsFailed, engagementId, message: (result as { message?: string }).message });
            }
        },
    SetReconnectRequired,
    ConnectWithPat,
    CheckConnectionStatus,
};
